import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { DateRange } from "react-day-picker";
import {
  ArrowRight,
  BarChart3,
  Check,
  Coins,
  Crown,
  Info,
  MoreHorizontal,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { HandleError } from "@/api/axiosClient";
import {
  InviteCodesApi,
  type PerkPointInviteCode,
  type UpdateInviteCodePayload,
} from "@/api/perkpoint/inviteCodes";
import {
  MembersApi,
  type ApiMemberRow,
} from "@/api/perkpoint/members";
import {
  TiersApi,
  type PerkPointTierApiItem,
  type PerkPointTiersSummary,
} from "@/api/perkpoint/tiers";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DatePickerWithRange,
  perkDatePresetRange,
} from "@/components/ui/date-range-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useActiveProgramId } from "../programs/program-scope";

const TIER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "earned", label: "Earned" },
  { value: "invite_only", label: "Invite only" },
];

function parseEarnRate(value: string): number {
  const parsed = Number(String(value).replace(/x$/i, "").trim());
  return Number.isFinite(parsed) ? parsed : NaN;
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.replace(/,/g, "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : NaN;
}

type TierRow = {
  id: string;
  index: string;
  name: string;
  requirementPts: number;
  requirementLabel: string;
  startingTier?: boolean;
  members: number;
  percent: string;
  progressWidth: string;
  crownClass: string;
};

const TIER_ROWS: TierRow[] = [
  {
    id: "1",
    index: "01",
    name: "Bronze",
    requirementPts: 0,
    requirementLabel: "0 pts",
    startingTier: true,
    members: 6820,
    percent: "44.2%",
    progressWidth: "8%",
    crownClass: "text-orange-500",
  },
  {
    id: "2",
    index: "02",
    name: "Silver",
    requirementPts: 5000,
    requirementLabel: "5,000 pts",
    members: 5420,
    percent: "35.2%",
    progressWidth: "35%",
    crownClass: "text-slate-400",
  },
  {
    id: "3",
    index: "03",
    name: "Gold",
    requirementPts: 15000,
    requirementLabel: "15,000 pts",
    members: 2460,
    percent: "16.0%",
    progressWidth: "65%",
    crownClass: "text-amber-500",
  },
  {
    id: "4",
    index: "04",
    name: "Platinum",
    requirementPts: 30000,
    requirementLabel: "30,000 pts",
    members: 720,
    percent: "4.7%",
    progressWidth: "100%",
    crownClass: "text-sky-500",
  },
];

type LadderBadge = "Default" | "Earned" | "Invite only";

type LadderTierItem = {
  id: string;
  key: string;
  name: string;
  rank: number;
  accentColor: string;
  badge: LadderBadge;
  tierType: string;
  inviteOnly: boolean;
  description: string;
  entry: string;
  keepWith: string;
  earnRate: string;
  welcomeBonusPoints: string;
  benefits: { name: string; value: string }[];
  thresholds: {
    metric: string;
    entry_value: number;
    retention_value: number | null;
  }[];
};

function formatMetricLabel(metric: string): string {
  if (!metric) return "";
  return metric.charAt(0).toUpperCase() + metric.slice(1);
}

function formatThresholdField(
  thresholds: PerkPointTierApiItem["thresholds"] | undefined,
  field: "entry_value" | "retention_value",
): string {
  if (!thresholds?.length) return "—";
  const parts = thresholds
    .map((item) => {
      const value = item[field];
      if (value === null || value === undefined) return null;
      return `${formatMetricLabel(item.metric)} ${Number(value).toLocaleString("en-IN")}`;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function mapTierBadge(tier: PerkPointTierApiItem): LadderBadge {
  if (tier.invite_only || tier.tier_type === "invite_only") return "Invite only";
  if (tier.tier_type === "default") return "Default";
  return "Earned";
}

function mapApiTierToLadderItem(tier: PerkPointTierApiItem): LadderTierItem {
  const earnRate =
    tier.earn_rate === null || tier.earn_rate === undefined
      ? "—"
      : `${tier.earn_rate}x`;
  const welcomeBonusPoints =
    tier.welcome_bonus_points === null ||
    tier.welcome_bonus_points === undefined
      ? "—"
      : Number(tier.welcome_bonus_points).toLocaleString("en-IN");

  return {
    id: tier.id,
    key: tier.key,
    name: tier.name,
    rank: tier.rank,
    accentColor: tier.display_color || "#94A3B8",
    badge: mapTierBadge(tier),
    tierType: tier.tier_type || "",
    inviteOnly: Boolean(tier.invite_only),
    description: tier.description || "",
    entry: formatThresholdField(tier.thresholds, "entry_value"),
    keepWith: formatThresholdField(tier.thresholds, "retention_value"),
    earnRate,
    welcomeBonusPoints,
    benefits: Array.isArray(tier.benefits) ? tier.benefits : [],
    thresholds: Array.isArray(tier.thresholds) ? tier.thresholds : [],
  };
}

function LadderBadgePill({ badge }: { badge: LadderBadge }) {
  const styles: Record<LadderBadge, string> = {
    Default: "bg-slate-100 text-slate-600",
    Earned: "bg-green-50 text-green-700",
    "Invite only": "bg-violet-50 text-violet-700",
  };

  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        styles[badge],
      )}
    >
      {badge}
    </span>
  );
}

function ViewTierDetailsSheet({
  tier,
  open,
  onOpenChange,
}: {
  tier: LadderTierItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <div className="px-5 pb-4 pt-6 sm:px-6">
          <div className="pr-8">
            <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
              {tier?.name || "Tier details"}
            </SheetTitle>
            <SheetDescription className="mt-1 text-sm text-muted-foreground">
              Full details for this loyalty tier.
            </SheetDescription>
          </div>

          {tier ? (
            <div className="mt-6 space-y-5">
              <div
                className="h-2 w-full rounded-full"
                style={{ backgroundColor: tier.accentColor }}
              />

              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Tier code</dt>
                  <dd className="font-medium text-slate-900">{tier.key}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Rank</dt>
                  <dd className="font-medium text-slate-900">{tier.rank}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Tier type</dt>
                  <dd className="font-medium text-slate-900">
                    <LadderBadgePill badge={tier.badge} />
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Invite only</dt>
                  <dd className="font-medium text-slate-900">
                    {tier.inviteOnly ? "Yes" : "No"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Display color</dt>
                  <dd className="flex items-center gap-2 font-mono text-slate-900">
                    <span
                      className="h-4 w-4 rounded-full border border-slate-200"
                      style={{ backgroundColor: tier.accentColor }}
                    />
                    {tier.accentColor}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Earn rate</dt>
                  <dd className="font-medium text-slate-900">{tier.earnRate}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Welcome bonus</dt>
                  <dd className="font-medium text-slate-900">
                    {tier.welcomeBonusPoints}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Description</dt>
                  <dd className="mt-1 text-slate-800">
                    {tier.description || "—"}
                  </dd>
                </div>
              </dl>

              <div className="space-y-2 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900">
                  Thresholds
                </h4>
                {tier.thresholds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No thresholds</p>
                ) : (
                  <div className="space-y-2">
                    {tier.thresholds.map((item) => (
                      <div
                        key={`${item.metric}-${item.entry_value}`}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                      >
                        <p className="font-medium text-slate-900">
                          {formatMetricLabel(item.metric)}
                        </p>
                        <p className="mt-1 text-muted-foreground">
                          Entry: {Number(item.entry_value).toLocaleString("en-IN")}
                          {" · "}
                          Keep with:{" "}
                          {item.retention_value === null ||
                          item.retention_value === undefined
                            ? "—"
                            : Number(item.retention_value).toLocaleString(
                                "en-IN",
                              )}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900">
                  Benefits
                </h4>
                {tier.benefits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No benefits</p>
                ) : (
                  <div className="space-y-2">
                    {tier.benefits.map((benefit) => (
                      <div
                        key={`${benefit.name}-${benefit.value}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                      >
                        <span className="font-medium text-slate-900">
                          {benefit.name}
                        </span>
                        <span className="text-muted-foreground">
                          {benefit.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function buildBenefitMatrix(tiers: LadderTierItem[]) {
  const benefitNames: string[] = [];
  const seen = new Set<string>();
  for (const tier of tiers) {
    for (const benefit of tier.benefits) {
      const name = benefit.name?.trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      benefitNames.push(name);
    }
  }

  return benefitNames.map((name) => ({
    benefit: name,
    values: tiers.map((tier) => {
      const match = tier.benefits.find(
        (item) => item.name.trim() === name,
      );
      return match?.value?.trim() || "";
    }),
  }));
}

function BenefitValue({ value }: { value: string }) {
  if (!value || value === "—" || value.toLowerCase() === "dash") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (value.toLowerCase() === "check" || value === "✓") {
    return <Check className="mx-auto h-4 w-4 text-green-600" />;
  }
  if (value.toLowerCase() === "never") {
    return <span className="font-medium text-green-700">Never</span>;
  }
  return <span className="text-slate-800">{value}</span>;
}

const INVITE_FLOW_STEPS = [
  "Admin creates code",
  "Code sent to invitee",
  "Invitee enrols on Dummy Program with code",
  "API checks expiry, cap, invitee",
  "Placed in tier, perks issued",
];

function formatInviteDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toDateInputValue(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 10);
  }
  return date.toISOString().slice(0, 10);
}

/** API expects a future datetime, e.g. 2026-12-31T23:59:59+04:00 — not a bare YYYY-MM-DD. */
function toLockedUntilDateTime(dateValue: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 0);
  if (Number.isNaN(endOfDay.getTime())) return null;
  const offsetMinutes = -endOfDay.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(abs / 60)).padStart(2, "0");
  const offsetMins = String(abs % 60).padStart(2, "0");
  const y = String(endOfDay.getFullYear()).padStart(4, "0");
  const m = String(endOfDay.getMonth() + 1).padStart(2, "0");
  const d = String(endOfDay.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T23:59:59${sign}${offsetHours}:${offsetMins}`;
}

function normalizeInviteRestrictMode(value: string | null | undefined): string {
  if (value === "email" || value === "phone") return value;
  return "anyone";
}

function buildInviteCodeUpdatePayload(
  source: PerkPointInviteCode,
  next: {
    type: "single_use" | "multi_use";
    tier_key: string;
    joining_perks: string | null;
    restricted_to: "anyone" | "email" | "phone";
    invitee_detail: string | null;
    tier_lock_until: string | null;
    valid_until: string;
    usage_cap: number;
  },
): UpdateInviteCodePayload {
  const payload: UpdateInviteCodePayload = {};

  const originalType = (source.type || source.code_type || "").trim();
  if (next.type !== originalType) payload.type = next.type;

  if (next.tier_key !== source.tier_key) payload.tier_key = next.tier_key;

  const originalPerks = formatJoiningPerks(source);
  const originalPerksNorm =
    originalPerks === "—" ? null : originalPerks.trim() || null;
  if (next.joining_perks !== originalPerksNorm) {
    payload.joining_perks = next.joining_perks;
  }

  const originalRestricted = normalizeInviteRestrictMode(source.restricted_to);
  const originalInvitee = source.invitee_detail?.trim() || null;
  if (
    next.restricted_to !== originalRestricted ||
    next.invitee_detail !== originalInvitee
  ) {
    payload.restricted_to = next.restricted_to;
    payload.invitee_detail = next.invitee_detail;
  }

  const originalLock = toDateInputValue(source.tier_lock_until) || null;
  if (next.tier_lock_until !== originalLock) {
    payload.tier_lock_until = next.tier_lock_until;
  }

  const originalValid = toDateInputValue(source.valid_until);
  if (next.valid_until !== originalValid) {
    payload.valid_until = next.valid_until;
  }

  if (next.usage_cap !== Number(source.usage_cap)) {
    payload.usage_cap = next.usage_cap;
  }

  return payload;
}

function formatInviteDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatInviteType(type: string): string {
  if (type === "single_use") return "Single use";
  if (type === "multi_use") return "Multi use";
  return type
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatRestrictedTo(value: string): string {
  if (value === "anyone") return "Anyone with code";
  if (value === "email") return "Email";
  if (value === "phone") return "Phone";
  return value;
}

function formatJoiningPerks(code: PerkPointInviteCode): string {
  if (code.joining_perks_label?.trim()) return code.joining_perks_label.trim();
  if (typeof code.joining_perks === "string" && code.joining_perks.trim()) {
    return code.joining_perks.trim();
  }
  if (
    code.joining_perks &&
    typeof code.joining_perks === "object" &&
    code.joining_perks.description?.trim()
  ) {
    return code.joining_perks.description.trim();
  }
  return "—";
}

function formatUsedLabel(code: PerkPointInviteCode): string {
  if (code.used_label?.trim()) return code.used_label.trim();
  const used = code.redemption_count ?? 0;
  const cap = code.max_redemptions ?? code.usage_cap;
  if (cap === null || cap === undefined) return String(used);
  return `${used} / ${cap}`;
}

function InviteStatusBadge({ status }: { status: string }) {
  const normalized = status.trim().toLowerCase();
  const label = status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  const styles: Record<string, string> = {
    active: "bg-emerald-50 text-emerald-700",
    redeemed: "bg-green-50 text-green-700",
    sent: "bg-sky-50 text-sky-700",
    expired: "bg-slate-100 text-slate-600",
    cancelled: "bg-red-50 text-red-700",
  };

  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium",
        styles[normalized] || "bg-slate-100 text-slate-600",
      )}
    >
      {label}
    </span>
  );
}

function InviteDetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <dt className="shrink-0 text-sm font-medium text-slate-500">{label}</dt>
      <dd
        className={cn(
          "min-w-0 text-right text-sm font-semibold text-slate-900",
          mono && "break-all font-mono text-xs font-medium text-slate-700",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function ViewInviteCodeDetailsSheet({
  code,
  open,
  onOpenChange,
}: {
  code: PerkPointInviteCode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const used = code?.redemption_count ?? 0;
  const cap = code?.max_redemptions ?? code?.usage_cap ?? 0;
  const usagePercent =
    cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto border-l border-slate-200 bg-white p-0 sm:max-w-lg"
      >
        <div className="border-b border-green-100 bg-green-50/70 px-5 pb-5 pt-6 sm:px-6">
          <div className="pr-8">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-green-700">
              Invite code
            </p>
            <SheetTitle className="mt-2 break-all font-mono text-2xl font-bold tracking-tight text-slate-900">
              {code?.code || "Invite code details"}
            </SheetTitle>
            <SheetDescription className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              {code ? (
                <>
                  <InviteStatusBadge status={code.status} />
                  <span className="text-green-300">·</span>
                  <span>
                    {formatInviteType(code.type || code.code_type || "")}
                  </span>
                </>
              ) : (
                "Full details for this invite code."
              )}
            </SheetDescription>
          </div>
        </div>

        {code ? (
          <div className="space-y-4 px-5 py-5 sm:px-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-green-100 bg-green-50/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-green-700">
                  Used
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900">
                  {formatUsedLabel(code)}
                </p>
              </div>
              <div className="rounded-xl border border-green-100 bg-green-50/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-green-700">
                  Tier
                </p>
                <p className="mt-1 truncate text-lg font-bold text-slate-900">
                  {code.tier_name || "—"}
                </p>
              </div>
              <div className="rounded-xl border border-green-100 bg-green-50/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-green-700">
                  Cap
                </p>
                <p className="mt-1 text-lg font-bold text-slate-900">
                  {code.usage_cap ?? "—"}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-green-100 bg-white p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">
                  Redemption progress
                </p>
                <p className="text-xs font-semibold text-green-700">
                  {usagePercent}%
                </p>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-green-100">
                <div
                  className="h-full rounded-full bg-green-600 transition-all"
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {used} of {cap || "—"} redemptions used
              </p>
            </div>

            <section className="overflow-hidden rounded-xl border border-green-100 bg-white">
              <div className="border-b border-green-100 bg-green-600 px-4 py-2.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-white">
                  Placement & perks
                </h4>
              </div>
              <dl className="px-4">
                <InviteDetailRow
                  label="Tier"
                  value={
                    <span>
                      {code.tier_name || "—"}
                      {code.tier_key ? (
                        <span className="mt-0.5 block text-xs font-normal text-slate-500">
                          key: {code.tier_key}
                        </span>
                      ) : null}
                    </span>
                  }
                />
                <InviteDetailRow
                  label="Joining perks"
                  value={formatJoiningPerks(code)}
                />
                <InviteDetailRow
                  label="Restricted to"
                  value={formatRestrictedTo(code.restricted_to)}
                />
                <InviteDetailRow
                  label="Invitee detail"
                  value={code.invitee_detail?.trim() || "—"}
                />
              </dl>
            </section>

            <section className="overflow-hidden rounded-xl border border-green-100 bg-white">
              <div className="border-b border-green-100 bg-green-600 px-4 py-2.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-white">
                  Validity
                </h4>
              </div>
              <dl className="px-4">
                <InviteDetailRow
                  label="Tier locked until"
                  value={formatInviteDate(code.tier_lock_until)}
                />
                <InviteDetailRow
                  label="Valid until"
                  value={formatInviteDate(code.valid_until)}
                />
                <InviteDetailRow
                  label="Created at"
                  value={formatInviteDateTime(code.created_at)}
                />
              </dl>
            </section>

            <section className="overflow-hidden rounded-xl border border-green-100 bg-white">
              <div className="border-b border-green-100 bg-green-600 px-4 py-2.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-white">
                  Identifiers
                </h4>
              </div>
              <dl className="px-4">
                <InviteDetailRow label="Code ID" value={code.id} mono />
                <InviteDetailRow
                  label="Tier ID"
                  value={code.tier_id || "—"}
                  mono
                />
                <InviteDetailRow
                  label="Program ID"
                  value={code.program_id}
                  mono
                />
              </dl>
            </section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function FieldLabel({
  children,
  required,
  htmlFor,
}: {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
}) {
  return (
    <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">
      {children}
      {required ? <span className="ml-0.5 text-red-500">*</span> : null}
    </Label>
  );
}

function AddTierSheet({
  open,
  onOpenChange,
  onSaved,
  tier = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
  tier?: LadderTierItem | null;
}) {
  const { programId } = useActiveProgramId();
  const isEditMode = Boolean(tier);
  const [tierName, setTierName] = useState("");
  const [tierCode, setTierCode] = useState("");
  const [rank, setRank] = useState("");
  const [description, setDescription] = useState("");
  const [tierType, setTierType] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [thresholds, setThresholds] = useState<
    { id: string; metric: string; entryValue: string; retentionValue: string }[]
  >([{ id: "1", metric: "", entryValue: "", retentionValue: "" }]);
  const [earnRate, setEarnRate] = useState("");
  const [welcomeBonus, setWelcomeBonus] = useState("");
  const [customBenefits, setCustomBenefits] = useState<
    { id: string; name: string; value: string }[]
  >([{ id: "1", name: "", value: "" }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setTierName("");
    setTierCode("");
    setRank("");
    setDescription("");
    setTierType("");
    setAccentColor("");
    setThresholds([{ id: "1", metric: "", entryValue: "", retentionValue: "" }]);
    setEarnRate("");
    setWelcomeBonus("");
    setCustomBenefits([{ id: "1", name: "", value: "" }]);
  };

  const populateForm = (source: LadderTierItem) => {
    setTierName(source.name);
    setTierCode(source.key);
    setRank(String(source.rank));
    setDescription(source.description || "");
    setTierType(
      source.inviteOnly
        ? "invite_only"
        : source.tierType || (source.badge === "Default" ? "default" : "earned"),
    );
    setAccentColor(source.accentColor || "");
    setEarnRate(source.earnRate === "—" ? "" : source.earnRate.replace(/x$/i, ""));
    setWelcomeBonus(
      source.welcomeBonusPoints === "—"
        ? ""
        : source.welcomeBonusPoints.replace(/,/g, ""),
    );
    setThresholds(
      source.thresholds.length > 0
        ? source.thresholds.map((item, index) => ({
            id: `${source.id}-th-${index}`,
            metric: item.metric || "",
            entryValue: String(item.entry_value ?? ""),
            retentionValue:
              item.retention_value === null || item.retention_value === undefined
                ? ""
                : String(item.retention_value),
          }))
        : [{ id: "1", metric: "", entryValue: "", retentionValue: "" }],
    );
    setCustomBenefits(
      source.benefits.length > 0
        ? source.benefits.map((item, index) => ({
            id: `${source.id}-bf-${index}`,
            name: item.name || "",
            value: item.value || "",
          }))
        : [{ id: "1", name: "", value: "" }],
    );
  };

  useEffect(() => {
    if (!open) return;
    if (tier) populateForm(tier);
    else resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tier]);

  const closeSheet = () => {
    if (isSubmitting) return;
    resetForm();
    onOpenChange(false);
  };

  const addThreshold = () => {
    setThresholds((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        metric: "",
        entryValue: "",
        retentionValue: "",
      },
    ]);
  };

  const removeThreshold = (id: string) => {
    setThresholds((prev) =>
      prev.length <= 1 ? prev : prev.filter((item) => item.id !== id),
    );
  };

  const updateThreshold = (
    id: string,
    field: "metric" | "entryValue" | "retentionValue",
    value: string,
  ) => {
    setThresholds((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addBenefit = () => {
    setCustomBenefits((prev) => [
      ...prev,
      { id: `${Date.now()}`, name: "", value: "" },
    ]);
  };

  const removeBenefit = (id: string) => {
    setCustomBenefits((prev) =>
      prev.length <= 1 ? prev : prev.filter((item) => item.id !== id),
    );
  };

  const updateBenefit = (
    id: string,
    field: "name" | "value",
    value: string,
  ) => {
    setCustomBenefits((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!programId) {
      toast.error(
        isEditMode
          ? "Select a program before updating a tier"
          : "Select a program before creating a tier",
      );
      return;
    }
    if (!tierName.trim()) {
      toast.error("Tier name is required");
      return;
    }
    if (!tierCode.trim()) {
      toast.error("Tier code is required");
      return;
    }
    const rankNumber = Number(rank);
    if (!rank.trim() || !Number.isFinite(rankNumber) || rankNumber < 1) {
      toast.error("Rank must be a valid number");
      return;
    }
    if (!tierType) {
      toast.error("Tier type is required");
      return;
    }
    const earnRateNumber = parseEarnRate(earnRate);
    if (!earnRate.trim() || Number.isNaN(earnRateNumber)) {
      toast.error("Earn rate must be a valid number");
      return;
    }
    const welcomeBonusNumber = parseOptionalNumber(welcomeBonus);
    if (Number.isNaN(welcomeBonusNumber)) {
      toast.error("Welcome bonus points must be a valid number");
      return;
    }

    const mappedThresholds = thresholds
      .filter(
        (item) =>
          item.metric &&
          item.entryValue.trim() !== "" &&
          Number.isFinite(Number(item.entryValue)),
      )
      .map((item) => ({
        metric: item.metric,
        entry_value: Number(item.entryValue),
        retention_value:
          item.retentionValue.trim() === ""
            ? null
            : Number(item.retentionValue),
      }));

    if (
      mappedThresholds.some(
        (item) =>
          item.retention_value !== null && Number.isNaN(item.retention_value),
      )
    ) {
      toast.error("Keep it with must be a valid number");
      return;
    }

    if (mappedThresholds.length === 0) {
      toast.error("Add at least one threshold with metrics and entry value");
      return;
    }

    const mappedBenefits = customBenefits
      .filter((item) => item.name.trim() || item.value.trim())
      .map((item) => ({
        name: item.name.trim(),
        value: item.value.trim(),
      }));

    const sharedPayload = {
      name: tierName.trim(),
      rank: rankNumber,
      tier_type: tierType,
      invite_only: tierType === "invite_only",
      display_color: accentColor || null,
      description: description.trim() || null,
      earn_rate: earnRateNumber,
      welcome_bonus_points: welcomeBonusNumber,
      benefits: mappedBenefits,
      thresholds: mappedThresholds,
    };

    setIsSubmitting(true);
    try {
      if (isEditMode && tier) {
        await TiersApi.update(programId, tier.id, sharedPayload);
        toast.success("Tier updated successfully");
      } else {
        await TiersApi.create(programId, {
          ...sharedPayload,
          key: tierCode.trim().toLowerCase(),
        });
        toast.success("Tier created successfully");
      }
      resetForm();
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(
        HandleError(error) ||
          (isEditMode ? "Failed to update tier" : "Failed to create tier"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next && isSubmitting) return;
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
          <div className="pr-8">
            <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
              {isEditMode ? "Edit tier" : "Add tier"}
            </SheetTitle>
            <SheetDescription className="mt-1 text-sm text-muted-foreground">
              {isEditMode
                ? "Update this loyalty tier and its benefits."
                : "Create a new loyalty tier for the ladder and define its benefits."}
            </SheetDescription>
          </div>

          <div className="mt-6 space-y-6">
            <section className="space-y-4">
              <h3 className="text-base font-semibold text-slate-900">
                Tier details
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel htmlFor="tier-name" required>
                    Tier name
                  </FieldLabel>
                  <Input
                    id="tier-name"
                    value={tierName}
                    onChange={(e) => setTierName(e.target.value)}
                    placeholder="e.g. Gold"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="tier-code" required>
                    Tier code
                  </FieldLabel>
                  <Input
                    id="tier-code"
                    value={tierCode}
                    onChange={(e) => setTierCode(e.target.value)}
                    placeholder="e.g. gold"
                    className="h-10"
                    disabled={isEditMode}
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="tier-rank" required>
                    Rank
                  </FieldLabel>
                  <Input
                    id="tier-rank"
                    type="number"
                    min={1}
                    value={rank}
                    onChange={(e) => setRank(e.target.value)}
                    placeholder="e.g. 1"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel htmlFor="tier-description">Description</FieldLabel>
                  <Textarea
                    id="tier-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short description of this tier"
                    className="min-h-[90px] resize-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Tier type</FieldLabel>
                  <Select
                    value={tierType || undefined}
                    onValueChange={setTierType}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIER_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="tier-accent-color">
                    Accent color
                  </FieldLabel>
                  <div className="flex h-10 items-center gap-2 rounded-md border border-input bg-white px-2">
                    <input
                      id="tier-accent-color"
                      type="color"
                      value={accentColor || "#A0AEC0"}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                      aria-label="Pick accent color"
                    />
                    <Input
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      placeholder="#A0AEC0"
                      className="h-8 border-0 px-1 font-mono text-sm shadow-none focus-visible:ring-0"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="tier-earn" required>
                    Earn rate
                  </FieldLabel>
                  <Input
                    id="tier-earn"
                    value={earnRate}
                    onChange={(e) => setEarnRate(e.target.value)}
                    placeholder="e.g. 1.5x"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="tier-welcome">
                    Welcome bonus points
                  </FieldLabel>
                  <Input
                    id="tier-welcome"
                    value={welcomeBonus}
                    onChange={(e) => setWelcomeBonus(e.target.value)}
                    placeholder="e.g. 1,000"
                    className="h-10"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Thresholds
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Add one or more metrics with entry and retention values.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addThreshold}
                  className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-green-700 hover:text-green-800"
                >
                  <Plus className="h-4 w-4" />
                  Add threshold
                </button>
              </div>

              <div className="space-y-3">
                {thresholds.map((threshold, index) => (
                  <div
                    key={threshold.id}
                    className="rounded-xl border border-slate-200 bg-slate-50/50 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-700">
                        Threshold {index + 1}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-red-600"
                        onClick={() => removeThreshold(threshold.id)}
                        disabled={thresholds.length <= 1}
                        aria-label={`Remove threshold ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5 sm:col-span-2">
                        <FieldLabel required>Metrics</FieldLabel>
                        <Select
                          value={threshold.metric || undefined}
                          onValueChange={(value) =>
                            updateThreshold(threshold.id, "metric", value)
                          }
                        >
                          <SelectTrigger className="h-10 bg-white">
                            <SelectValue placeholder="Select metrics" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spend">Spend</SelectItem>
                            <SelectItem value="bookings">Bookings</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel
                          htmlFor={`tier-entry-${threshold.id}`}
                          required
                        >
                          Entry requirement
                        </FieldLabel>
                        <Input
                          id={`tier-entry-${threshold.id}`}
                          type="number"
                          value={threshold.entryValue}
                          onChange={(e) =>
                            updateThreshold(
                              threshold.id,
                              "entryValue",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. 3000000"
                          className="h-10 bg-white"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel htmlFor={`tier-keep-${threshold.id}`}>
                          Keep it with
                        </FieldLabel>
                        <Input
                          id={`tier-keep-${threshold.id}`}
                          type="number"
                          value={threshold.retentionValue}
                          onChange={(e) =>
                            updateThreshold(
                              threshold.id,
                              "retentionValue",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. 2000000"
                          className="h-10 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Tier benefits
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Add custom benefit points for this tier. You can add or
                    remove rows as needed.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addBenefit}
                  className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-green-700 hover:text-green-800"
                >
                  <Plus className="h-4 w-4" />
                  Add benefit
                </button>
              </div>

              <div className="space-y-3">
                {customBenefits.map((benefit, index) => (
                  <div
                    key={benefit.id}
                    className="rounded-xl border border-slate-200 bg-slate-50/50 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-700">
                        Benefit {index + 1}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-red-600"
                        onClick={() => removeBenefit(benefit.id)}
                        disabled={customBenefits.length <= 1}
                        aria-label={`Remove benefit ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <FieldLabel htmlFor={`benefit-name-${benefit.id}`}>
                          Benefit name
                        </FieldLabel>
                        <Input
                          id={`benefit-name-${benefit.id}`}
                          value={benefit.name}
                          onChange={(e) =>
                            updateBenefit(benefit.id, "name", e.target.value)
                          }
                          placeholder="e.g. Hotel discount"
                          className="h-10 bg-white"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel htmlFor={`benefit-value-${benefit.id}`}>
                          Benefit value
                        </FieldLabel>
                        <Input
                          id={`benefit-value-${benefit.id}`}
                          value={benefit.value}
                          onChange={(e) =>
                            updateBenefit(benefit.id, "value", e.target.value)
                          }
                          placeholder="e.g. 10%"
                          className="h-10 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={closeSheet}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1 bg-green-600 text-white hover:bg-green-700"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode
                ? "Updating…"
                : "Creating…"
              : isEditMode
                ? "Update tier"
                : "Create tier"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CreateInviteCodeSheet({
  open,
  onOpenChange,
  tiers,
  onSaved,
  inviteCode = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tiers: LadderTierItem[];
  onSaved?: () => void;
  inviteCode?: PerkPointInviteCode | null;
}) {
  const { programId } = useActiveProgramId();
  const isEditMode = Boolean(inviteCode);
  const [code, setCode] = useState("");
  const [type, setType] = useState("");
  const [tierKey, setTierKey] = useState("");
  const [joiningPerks, setJoiningPerks] = useState("");
  const [restrictMode, setRestrictMode] = useState("");
  const [inviteeDetail, setInviteeDetail] = useState("");
  const [tierLockedUntil, setTierLockedUntil] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [usageCap, setUsageCap] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setCode("");
    setType("");
    setTierKey("");
    setJoiningPerks("");
    setRestrictMode("");
    setInviteeDetail("");
    setTierLockedUntil("");
    setValidUntil("");
    setUsageCap("");
  };

  const populateForm = (source: PerkPointInviteCode) => {
    setCode(source.code || "");
    setType(
      source.type === "multi_use" || source.code_type === "multi_use"
        ? "multi_use"
        : "single_use",
    );
    setTierKey(source.tier_key || "");
    const perks = formatJoiningPerks(source);
    setJoiningPerks(perks === "—" ? "" : perks);
    setRestrictMode(normalizeInviteRestrictMode(source.restricted_to));
    setInviteeDetail(source.invitee_detail?.trim() || "");
    setTierLockedUntil(toDateInputValue(source.tier_lock_until));
    setValidUntil(toDateInputValue(source.valid_until));
    setUsageCap(
      source.usage_cap === null || source.usage_cap === undefined
        ? ""
        : String(source.usage_cap),
    );
  };

  useEffect(() => {
    if (!open) return;
    if (inviteCode) populateForm(inviteCode);
    else resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inviteCode]);

  const closeSheet = () => {
    if (isSubmitting) return;
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!programId) {
      toast.error(
        isEditMode
          ? "Select a program before updating an invite code"
          : "Select a program before creating an invite code",
      );
      return;
    }
    if (!code.trim()) {
      toast.error("Code is required");
      return;
    }
    if (type !== "single_use" && type !== "multi_use") {
      toast.error("Type is required");
      return;
    }
    if (!tierKey) {
      toast.error("Tier is required");
      return;
    }
    if (!validUntil.trim()) {
      toast.error("Valid until is required");
      return;
    }
    if (
      (restrictMode === "email" || restrictMode === "phone") &&
      !inviteeDetail.trim()
    ) {
      toast.error(
        restrictMode === "email"
          ? "Invitee email is required"
          : "Invitee phone is required",
      );
      return;
    }

    const capParsed =
      usageCap.trim() === ""
        ? type === "single_use"
          ? 1
          : NaN
        : Number(usageCap);
    if (!Number.isFinite(capParsed) || capParsed < 1) {
      toast.error(
        type === "multi_use"
          ? "Usage cap is required for multi-use codes"
          : "Usage cap must be a valid number",
      );
      return;
    }

    const restrictedTo =
      restrictMode === "email" || restrictMode === "phone"
        ? restrictMode
        : "anyone";

    const sharedPayload = {
      type: type as "single_use" | "multi_use",
      tier_key: tierKey,
      joining_perks: joiningPerks.trim() || null,
      restricted_to: restrictedTo as "anyone" | "email" | "phone",
      invitee_detail:
        restrictedTo === "anyone" ? null : inviteeDetail.trim() || null,
      tier_lock_until: tierLockedUntil.trim() || null,
      valid_until: validUntil.trim(),
      usage_cap: capParsed,
    };

    setIsSubmitting(true);
    try {
      if (isEditMode && inviteCode) {
        const updatePayload = buildInviteCodeUpdatePayload(
          inviteCode,
          sharedPayload,
        );
        if (Object.keys(updatePayload).length === 0) {
          toast.message("No changes to update");
          return;
        }
        await InviteCodesApi.update(programId, inviteCode.id, updatePayload);
        toast.success("Invite code updated successfully");
      } else {
        await InviteCodesApi.create(programId, {
          ...sharedPayload,
          code: code.trim(),
        });
        toast.success("Invite code created successfully");
      }
      resetForm();
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(
        HandleError(error) ||
          (isEditMode
            ? "Failed to update invite code"
            : "Failed to create invite code"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next && isSubmitting) return;
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
          <div className="pr-8">
            <SheetTitle className="text-2xl font-bold tracking-tight text-slate-900">
              {isEditMode ? "Edit code" : "Create code"}
            </SheetTitle>
            <SheetDescription className="mt-1 text-sm text-muted-foreground">
              {isEditMode
                ? "Update this invite code and its placement settings."
                : "Create an invite code to place members into a tier with optional joining perks."}
            </SheetDescription>
          </div>

          <div className="mt-6 space-y-6">
            <section className="space-y-4">
              <h3 className="text-base font-semibold text-slate-900">
                Code details
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel htmlFor="invite-code" required>
                    Code
                  </FieldLabel>
                  <Input
                    id="invite-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="e.g. WS-GOLD-M9X2"
                    className="h-10"
                    disabled={isEditMode}
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Type</FieldLabel>
                  <Select value={type || undefined} onValueChange={setType}>
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_use">Single use</SelectItem>
                      <SelectItem value="multi_use">Multi use</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>Tier</FieldLabel>
                  <Select
                    value={tierKey || undefined}
                    onValueChange={setTierKey}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiers.map((item) => (
                        <SelectItem key={item.id} value={item.key}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel htmlFor="invite-perks">Joining perks</FieldLabel>
                  <Input
                    id="invite-perks"
                    value={joiningPerks}
                    onChange={(e) => setJoiningPerks(e.target.value)}
                    placeholder="e.g. 1,000 pts + lounge pass"
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>Restricted to</FieldLabel>
                  <Select
                    value={restrictMode || undefined}
                    onValueChange={(value) => {
                      setRestrictMode(value);
                      if (value === "anyone") setInviteeDetail("");
                    }}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select restriction" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anyone">Anyone with code</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="invite-restrict-value">
                    Invitee detail
                  </FieldLabel>
                  <Input
                    id="invite-restrict-value"
                    value={inviteeDetail}
                    onChange={(e) => setInviteeDetail(e.target.value)}
                    placeholder={
                      restrictMode === "phone"
                        ? "e.g. +971 55 000 0000"
                        : restrictMode === "email"
                          ? "e.g. member@email.com"
                          : "Optional for anyone with code"
                    }
                    className="h-10"
                    disabled={restrictMode === "anyone" || !restrictMode}
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="invite-locked">
                    Tier locked until
                  </FieldLabel>
                  <Input
                    id="invite-locked"
                    type="date"
                    value={tierLockedUntil}
                    onChange={(e) => setTierLockedUntil(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="invite-valid" required>
                    Valid until
                  </FieldLabel>
                  <Input
                    id="invite-valid"
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    className="h-10"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel htmlFor="invite-cap">Usage cap</FieldLabel>
                  <Input
                    id="invite-cap"
                    type="number"
                    min={1}
                    value={usageCap}
                    onChange={(e) => setUsageCap(e.target.value)}
                    placeholder={
                      type === "multi_use"
                        ? "e.g. 50"
                        : "Defaults to 1 for single use"
                    }
                    className="h-10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown in the table as Used (e.g. 0 / 50).
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={closeSheet}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1 bg-green-600 text-white hover:bg-green-700"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode
                ? "Updating…"
                : "Creating…"
              : isEditMode
                ? "Update code"
                : "Create code"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function RewardsTiers() {
  const { programId } = useActiveProgramId();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    perkDatePresetRange("last30"),
  );
  const [afterChange, setAfterChange] = useState<"lock" | "standard">("lock");
  const [issueBonus, setIssueBonus] = useState(false);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(false);
  const [addTierOpen, setAddTierOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<LadderTierItem | null>(null);
  const [createCodeOpen, setCreateCodeOpen] = useState(false);
  const [editingInviteCode, setEditingInviteCode] =
    useState<PerkPointInviteCode | null>(null);
  const [changeTierOpen, setChangeTierOpen] = useState(false);
  const [changeTierMembers, setChangeTierMembers] = useState<ApiMemberRow[]>(
    [],
  );
  const [selectedChangeMemberId, setSelectedChangeMemberId] = useState("");
  const [isLoadingChangeTierMembers, setIsLoadingChangeTierMembers] =
    useState(false);
  const [isChangingTier, setIsChangingTier] = useState(false);
  const [moveToTier, setMoveToTier] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [lockedUntil, setLockedUntil] = useState("");
  const [ladderTiers, setLadderTiers] = useState<LadderTierItem[]>([]);
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);
  const [tierSummary, setTierSummary] = useState<PerkPointTiersSummary | null>(
    null,
  );
  const [inviteCodes, setInviteCodes] = useState<PerkPointInviteCode[]>([]);
  const [isLoadingInviteCodes, setIsLoadingInviteCodes] = useState(false);
  const [inviteDetailsOpen, setInviteDetailsOpen] = useState(false);
  const [selectedInviteCode, setSelectedInviteCode] =
    useState<PerkPointInviteCode | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<LadderTierItem | null>(
    null,
  );

  const fetchTiers = async () => {
    if (!programId) {
      setLadderTiers([]);
      return;
    }
    setIsLoadingTiers(true);
    try {
      const items = await TiersApi.list(programId);
      const mapped = [...items]
        .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
        .map(mapApiTierToLadderItem);
      setLadderTiers(mapped);
      if (!moveToTier && mapped[0]) setMoveToTier(mapped[0].key);
    } catch (error) {
      console.error("Error fetching tiers:", error);
      toast.error(HandleError(error) || "Failed to fetch tiers");
      setLadderTiers([]);
    } finally {
      setIsLoadingTiers(false);
    }
  };

  const fetchTierSummary = async () => {
    if (!programId) {
      setTierSummary(null);
      return;
    }
    try {
      const summary = await TiersApi.summary(programId);
      setTierSummary(summary);
    } catch (error) {
      console.error("Error fetching tiers summary:", error);
      setTierSummary(null);
    }
  };

  const fetchInviteCodes = async () => {
    if (!programId) {
      setInviteCodes([]);
      return;
    }
    setIsLoadingInviteCodes(true);
    try {
      const codes = await InviteCodesApi.list(programId);
      setInviteCodes(codes);
    } catch (error) {
      console.error("Error fetching invite codes:", error);
      toast.error(HandleError(error) || "Failed to fetch invite codes");
      setInviteCodes([]);
    } finally {
      setIsLoadingInviteCodes(false);
    }
  };

  useEffect(() => {
    void fetchTiers();
    void fetchTierSummary();
    void fetchInviteCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const selectedTierName =
    ladderTiers.find((tier) => tier.key === moveToTier)?.name ?? "tier";

  const benefitMatrix = buildBenefitMatrix(ladderTiers);

  const memberInitials = (name: string | null | undefined) => {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "M";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const selectedChangeMember =
    changeTierMembers.find(
      (member) => member.member_id === selectedChangeMemberId,
    ) ?? null;

  const openChangeTier = () => {
    setSelectedChangeMemberId("");
    setChangeTierMembers([]);
    setMoveToTier(ladderTiers[0]?.key || "");
    setChangeReason("");
    setLockedUntil("");
    setAfterChange("lock");
    setIssueBonus(false);
    setNotifyWhatsapp(false);
    setChangeTierOpen(true);
  };

  useEffect(() => {
    if (!changeTierOpen || !programId) {
      setChangeTierMembers([]);
      setIsLoadingChangeTierMembers(false);
      return;
    }

    let cancelled = false;
    const loadMembers = async () => {
      setIsLoadingChangeTierMembers(true);
      try {
        const result = await MembersApi.list(programId, {
          pageSize: 100,
          status: "active",
        });
        if (!cancelled) {
          setChangeTierMembers(result.items ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading members:", error);
          toast.error(HandleError(error) || "Failed to load members");
          setChangeTierMembers([]);
        }
      } finally {
        if (!cancelled) setIsLoadingChangeTierMembers(false);
      }
    };

    void loadMembers();
    return () => {
      cancelled = true;
    };
  }, [changeTierOpen, programId]);

  const openTierDetails = (tier: LadderTierItem) => {
    setSelectedTier(tier);
    setDetailsOpen(true);
  };

  const openEditTier = (tier: LadderTierItem) => {
    setEditingTier(tier);
    setAddTierOpen(true);
  };

  const openInviteCodeDetails = (code: PerkPointInviteCode) => {
    setSelectedInviteCode(code);
    setInviteDetailsOpen(true);
  };

  const openEditInviteCode = (code: PerkPointInviteCode) => {
    setEditingInviteCode(code);
    setCreateCodeOpen(true);
  };

  const handleTierSheetOpenChange = (open: boolean) => {
    setAddTierOpen(open);
    if (!open) setEditingTier(null);
  };

  const handleInviteCodeSheetOpenChange = (open: boolean) => {
    setCreateCodeOpen(open);
    if (!open) setEditingInviteCode(null);
  };

  const confirmChangeTier = async () => {
    if (!programId) {
      toast.error("Select a program before changing a member tier");
      return;
    }
    if (!selectedChangeMember?.member_id) {
      toast.error("Select a valid member first");
      return;
    }
    if (!moveToTier) {
      toast.error("Select a tier to move to");
      return;
    }
    if (!changeReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (afterChange === "lock" && !lockedUntil.trim()) {
      toast.error("Locked until date is required when locking the tier");
      return;
    }

    const lockedUntilDateTime =
      afterChange === "lock" ? toLockedUntilDateTime(lockedUntil.trim()) : null;
    if (afterChange === "lock") {
      if (!lockedUntilDateTime) {
        toast.error("Enter a valid locked-until date");
        return;
      }
      if (new Date(lockedUntilDateTime).getTime() <= Date.now()) {
        toast.error("Locked until must be a future date");
        return;
      }
    }

    setIsChangingTier(true);
    try {
      const result = await MembersApi.changeTier(
        programId,
        selectedChangeMember.member_id,
        afterChange === "lock"
          ? {
              tier_key: moveToTier,
              reason: changeReason.trim(),
              after_change: "lock",
              locked_until: lockedUntilDateTime!,
              issue_welcome_bonus: issueBonus,
              notify_whatsapp: notifyWhatsapp,
            }
          : {
              tier_key: moveToTier,
              reason: changeReason.trim(),
              after_change: "standard",
              issue_welcome_bonus: issueBonus,
              notify_whatsapp: notifyWhatsapp,
            },
      );
      const verb =
        result.direction === "downgrade"
          ? "downgraded"
          : result.direction === "upgrade"
            ? "upgraded"
            : "moved";
      toast.success(
        `${selectedChangeMember.name || "Member"} ${verb} to ${selectedTierName}.`,
      );
      setChangeTierOpen(false);
      void fetchTierSummary();
    } catch (error) {
      toast.error(HandleError(error) || "Failed to change member tier");
    } finally {
      setIsChangingTier(false);
    }
  };

  return (
    <div className="space-y-6 pb-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Loyalty tiers
          </h1>
          <p className="mt-1 text-muted-foreground">
            Set how members move from one tier to the next.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            type="button"
            onClick={openChangeTier}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            Change member tier
          </Button>
          <DatePickerWithRange
            presets
            date={dateRange}
            setDate={setDateRange}
            className="w-full sm:w-auto [&_button]:h-9 [&_button]:bg-white [&_button]:text-slate-700"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total tiers</p>
                <p className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900">
                  {tierSummary
                    ? Number(tierSummary.total_tiers).toLocaleString("en-IN")
                    : "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tierSummary?.tier_range?.label || "—"}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Coins className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Members enrolled</p>
                <p className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900">
                  {tierSummary
                    ? Number(tierSummary.members_enrolled).toLocaleString(
                        "en-IN",
                      )
                    : "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tierSummary?.members_enrolled_label || "Across all tiers"}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Users className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">
                  {tierSummary?.gold_plus?.from_tier_name
                    ? `Members in ${tierSummary.gold_plus.from_tier_name}+`
                    : "Members in Gold+"}
                </p>
                <p className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900">
                  {tierSummary?.gold_plus
                    ? Number(tierSummary.gold_plus.members).toLocaleString(
                        "en-IN",
                      )
                    : "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tierSummary?.gold_plus?.label || "—"}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <BarChart3 className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Top tier</p>
                <p className="mt-1.5 text-xl font-bold tracking-tight text-slate-900">
                  {tierSummary?.top_tier?.name || "—"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {tierSummary?.top_tier
                    ? `${Number(tierSummary.top_tier.members).toLocaleString("en-IN")} members`
                    : "—"}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Crown className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How members qualify */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              How members qualify
            </h2>
            <p className="text-sm text-muted-foreground">
              Applies to every tier below
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Qualification metrics
              </Label>
              <Select defaultValue="spend-or-bookings">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select metrics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spend-or-bookings">
                    Spend OR bookings (any of)
                  </SelectItem>
                  <SelectItem value="spend-and-bookings">
                    Spend AND bookings (all of)
                  </SelectItem>
                  <SelectItem value="tier-points">Tier points only</SelectItem>
                  <SelectItem value="nights-stayed">Nights stayed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Measured over
              </Label>
              <Select defaultValue="rolling-12">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rolling-12">Rolling 12 months</SelectItem>
                  <SelectItem value="calendar-year">Calendar year</SelectItem>
                  <SelectItem value="membership-anniversary">
                    Membership anniversary
                  </SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Downgrade check
              </Label>
              <Select defaultValue="monthly">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select schedule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly, 1st at 02:00</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="on-anniversary">On anniversary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="tier-guaranteed"
                className="text-sm font-medium text-slate-700"
              >
                Tier guaranteed for
              </Label>
              <Input
                id="tier-guaranteed"
                placeholder="e.g. 12 months after it is earned"
                className="h-10 bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                When retention is missed
              </Label>
              <Select defaultValue="drop-one">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="drop-one">Drop one tier</SelectItem>
                  <SelectItem value="drop-to-qualify">
                    Drop to tier they qualify for
                  </SelectItem>
                  <SelectItem value="drop-to-base">Drop to base tier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="grace-period"
                className="text-sm font-medium text-slate-700"
              >
                Grace period
              </Label>
              <Input
                id="grace-period"
                placeholder="e.g. 30 days, member is warned"
                className="h-10 bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Tier on enrolment
              </Label>
              <Select defaultValue="bronze">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2 lg:col-span-1 xl:col-span-1">
              <Label className="text-sm font-medium text-slate-700">
                Counts towards tier
              </Label>
              <Select defaultValue="net-booking">
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="net-booking">
                    Net booking value, after discounts
                  </SelectItem>
                  <SelectItem value="gross-booking">
                    Gross booking value
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Cancellations remove the credit
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier ladder */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Tier ladder</h2>
            <Button
              type="button"
              onClick={() => {
                setEditingTier(null);
                setAddTierOpen(true);
              }}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add tier
            </Button>
          </div>

          <div className="-mx-1 overflow-x-auto px-1 pb-1">
            {isLoadingTiers ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Loading tiers…
              </p>
            ) : ladderTiers.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No tiers yet. Create a tier to build the ladder.
              </p>
            ) : (
              <div className="flex min-w-max gap-5">
                {ladderTiers.map((tier) => (
                  <div
                    key={tier.id}
                    className="w-[300px] shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                  >
                    <div
                      className="h-1.5 w-full"
                      style={{ backgroundColor: tier.accentColor }}
                    />
                    <div className="flex flex-col gap-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold text-slate-900">
                            {tier.name}
                          </h3>
                          <p className="text-[11px] text-muted-foreground">
                            Rank {tier.rank} · {tier.key}
                          </p>
                        </div>
                        <LadderBadgePill badge={tier.badge} />
                      </div>

                      {tier.description ? (
                        <p className="line-clamp-2 text-xs text-slate-600">
                          {tier.description}
                        </p>
                      ) : null}

                      <div className="space-y-2 border-t border-slate-100 pt-2.5 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <span className="shrink-0 text-muted-foreground">
                            Entry
                          </span>
                          <span className="min-w-0 text-right font-medium leading-snug text-slate-800">
                            {tier.entry}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="shrink-0 text-muted-foreground">
                            Keep it with
                          </span>
                          <span className="min-w-0 text-right font-medium leading-snug text-slate-800">
                            {tier.keepWith}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="shrink-0 text-muted-foreground">
                            Earn rate
                          </span>
                          <span className="min-w-0 text-right font-medium leading-snug text-slate-800">
                            {tier.earnRate}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          className="text-xs font-medium text-green-700 hover:text-green-800"
                          onClick={() => openTierDetails(tier)}
                        >
                          View details
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-slate-600 hover:text-slate-900"
                          onClick={() => openEditTier(tier)}
                        >
                          Edit tier
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Benefits by tier */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Benefits by tier
            </h2>
            <p className="text-sm text-muted-foreground">
              Benefits configured on each loyalty tier
            </p>
          </div>

          {isLoadingTiers ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Loading benefits…
            </p>
          ) : ladderTiers.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No tiers yet. Add a tier to see benefits here.
            </p>
          ) : benefitMatrix.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No benefits configured on these tiers yet.
            </p>
          ) : (
            <div className="w-full overflow-x-auto rounded-lg border border-slate-200">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead className="min-w-[200px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Benefit
                    </TableHead>
                    {ladderTiers.map((tier) => (
                      <TableHead
                        key={tier.id}
                        className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="h-2.5 w-2.5 rounded-sm"
                            style={{ backgroundColor: tier.accentColor }}
                          />
                          {tier.name}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benefitMatrix.map((row) => (
                    <TableRow key={row.benefit}>
                      <TableCell className="font-medium text-slate-900">
                        {row.benefit}
                      </TableCell>
                      {row.values.map((value, index) => (
                        <TableCell
                          key={`${row.benefit}-${ladderTiers[index]?.id ?? index}`}
                          className="text-sm"
                        >
                          <BenefitValue value={value} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Invite codes */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">
                Invite codes
              </h2>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      aria-label="Invite code flow"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="max-w-[min(92vw,640px)] border-slate-200 bg-white p-3 text-slate-800 shadow-lg"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      {INVITE_FLOW_STEPS.map((step, index) => (
                        <div key={step} className="flex items-center gap-1.5">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                            {step}
                          </span>
                          {index < INVITE_FLOW_STEPS.length - 1 ? (
                            <ArrowRight className="h-3 w-3 shrink-0 text-slate-400" />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button
              type="button"
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={() => {
                setEditingInviteCode(null);
                setCreateCodeOpen(true);
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Create code
            </Button>
          </div>

          {isLoadingInviteCodes ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Loading invite codes…
            </p>
          ) : inviteCodes.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No invite codes yet. Create a code to get started.
            </p>
          ) : (
            <div className="w-full overflow-x-auto rounded-lg border border-slate-200">
              <Table className="min-w-[1100px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Code
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Type
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Tier
                    </TableHead>
                    <TableHead className="min-w-[160px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Joining perks
                    </TableHead>
                    <TableHead className="min-w-[140px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Restricted to
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Tier locked until
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Valid until
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Used
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inviteCodes.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium text-slate-900">
                        {row.code}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {formatInviteType(row.type || row.code_type || "")}
                      </TableCell>
                      <TableCell className="text-slate-800">
                        {row.tier_name || "—"}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {formatJoiningPerks(row)}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {row.invitee_detail?.trim()
                          ? row.invitee_detail
                          : formatRestrictedTo(row.restricted_to)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {formatInviteDate(row.tier_lock_until)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {formatInviteDate(row.valid_until)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-800">
                        {formatUsedLabel(row)}
                      </TableCell>
                      <TableCell>
                        <InviteStatusBadge status={row.status} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-slate-900"
                              aria-label={`Actions for ${row.code}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={() => openInviteCodeDetails(row)}
                            >
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openEditInviteCode(row)}
                            >
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <p className="mt-4 text-sm text-muted-foreground">
            Invalid codes return a specific error to the booking platform:
            not_found, expired, cap_reached or invitee_mismatch.
          </p>
        </CardContent>
      </Card>

      {/* Footer actions */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="outline" className="bg-white">
            Cancel
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-green-200 bg-white text-green-700 hover:bg-green-50 hover:text-green-800"
            >
              Save changes
            </Button>
            <Button
              type="button"
              className="bg-green-600 text-white hover:bg-green-700"
            >
              Publish changes
            </Button>
          </div>
        </CardContent>
      </Card>

      <Sheet
        open={changeTierOpen}
        onOpenChange={(open) => {
          if (isChangingTier) return;
          setChangeTierOpen(open);
          if (!open) {
            setSelectedChangeMemberId("");
            setChangeTierMembers([]);
          }
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
        >
          <div className="flex-1 overflow-y-auto px-5 pb-4 pt-6 sm:px-6">
            <div className="flex flex-col gap-1 pr-8 sm:flex-row sm:items-start sm:justify-between">
              <SheetTitle className="text-lg font-semibold text-slate-900">
                Change a member&apos;s tier manually
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground sm:pt-0.5 sm:text-right">
                Needs permission tier.manage · every change is audited
              </SheetDescription>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">
                    Member
                  </Label>
                  <Select
                    value={selectedChangeMemberId}
                    onValueChange={setSelectedChangeMemberId}
                    disabled={isLoadingChangeTierMembers}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue
                        placeholder={
                          isLoadingChangeTierMembers
                            ? "Loading members…"
                            : "Select a member"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="!max-h-[280px]">
                      {changeTierMembers.length === 0 &&
                      !isLoadingChangeTierMembers ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground">
                          No members found
                        </div>
                      ) : (
                        changeTierMembers.map((member) => (
                          <SelectItem
                            key={member.member_id}
                            value={member.member_id}
                          >
                            {member.name || "Unnamed member"}
                            {member.external_member_id
                              ? ` · ${member.external_member_id}`
                              : ""}
                            {member.tier?.name
                              ? ` · ${member.tier.name}`
                              : ""}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {selectedChangeMember ? (
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-green-100 text-sm font-semibold text-green-700">
                        {memberInitials(selectedChangeMember.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {selectedChangeMember.name || "Unnamed member"}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">
                        {selectedChangeMember.tier?.name || "No tier"}
                        {selectedChangeMember.email
                          ? ` · ${selectedChangeMember.email}`
                          : selectedChangeMember.external_member_id
                            ? ` · ${selectedChangeMember.external_member_id}`
                            : ""}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">
                    Move to tier
                  </Label>
                  <Select value={moveToTier} onValueChange={setMoveToTier}>
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {ladderTiers.map((tier) => (
                        <SelectItem key={tier.id} value={tier.key}>
                          {tier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="manual-reason"
                    className="text-sm font-medium text-slate-700"
                  >
                    Reason (required)
                  </Label>
                  <Textarea
                    id="manual-reason"
                    value={changeReason}
                    onChange={(event) => setChangeReason(event.target.value)}
                    placeholder="Enter reason for tier change"
                    className="min-h-[110px] resize-none bg-white"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    After the change
                  </Label>
                  <RadioGroup
                    value={afterChange}
                    onValueChange={(value) =>
                      setAfterChange(value as "lock" | "standard")
                    }
                    className="gap-3"
                  >
                    <label
                      htmlFor="after-lock"
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors",
                        afterChange === "lock"
                          ? "border-green-600 bg-green-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300",
                      )}
                    >
                      <RadioGroupItem
                        id="after-lock"
                        value="lock"
                        className="mt-0.5 border-green-600 text-green-600"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Lock the tier until a date
                        </span>
                        <span className="mt-0.5 block text-sm text-muted-foreground">
                          No automatic downgrade before then
                        </span>
                      </span>
                    </label>

                    <label
                      htmlFor="after-standard"
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors",
                        afterChange === "standard"
                          ? "border-green-600 bg-green-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300",
                      )}
                    >
                      <RadioGroupItem
                        id="after-standard"
                        value="standard"
                        className="mt-0.5 border-green-600 text-green-600"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Standard rules
                        </span>
                        <span className="mt-0.5 block text-sm text-muted-foreground">
                          Normal retention check from the next run
                        </span>
                      </span>
                    </label>
                  </RadioGroup>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="locked-until"
                    className="text-sm font-medium text-slate-700"
                  >
                    Locked until
                  </Label>
                  <Input
                    id="locked-until"
                    type="date"
                    value={lockedUntil}
                    min={(() => {
                      const now = new Date();
                      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
                    })()}
                    onChange={(event) => setLockedUntil(event.target.value)}
                    className="h-10 bg-white"
                    disabled={afterChange !== "lock"}
                  />
                </div>

                <div className="space-y-3 pt-1">
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      checked={issueBonus}
                      onCheckedChange={(checked) =>
                        setIssueBonus(Boolean(checked))
                      }
                      className="mt-0.5 data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600"
                    />
                    <span className="text-sm text-slate-800">
                      Issue {selectedTierName} welcome bonus
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      checked={notifyWhatsapp}
                      onCheckedChange={(checked) =>
                        setNotifyWhatsapp(Boolean(checked))
                      }
                      className="mt-0.5 data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600"
                    />
                    <span className="text-sm text-slate-800">
                      Notify member on WhatsApp
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <button
              type="button"
              className="text-left text-sm font-medium text-green-700 hover:text-green-800"
              onClick={() =>
                toast.message("Bulk change from CSV is not available yet.")
              }
            >
              Bulk change from CSV
            </button>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="bg-white"
                onClick={() => setChangeTierOpen(false)}
                disabled={isChangingTier}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-green-600 text-white hover:bg-green-700"
                disabled={
                  isChangingTier ||
                  !selectedChangeMember ||
                  !moveToTier ||
                  !changeReason.trim() ||
                  (afterChange === "lock" && !lockedUntil.trim())
                }
                onClick={() => void confirmChangeTier()}
              >
                {isChangingTier ? "Changing…" : "Change tier"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AddTierSheet
        open={addTierOpen}
        onOpenChange={handleTierSheetOpenChange}
        tier={editingTier}
        onSaved={() => {
          void fetchTiers();
          void fetchTierSummary();
        }}
      />
      <CreateInviteCodeSheet
        open={createCodeOpen}
        onOpenChange={handleInviteCodeSheetOpenChange}
        tiers={ladderTiers}
        inviteCode={editingInviteCode}
        onSaved={() => {
          void fetchInviteCodes();
        }}
      />
      <ViewTierDetailsSheet
        tier={selectedTier}
        open={detailsOpen}
        onOpenChange={(open) => {
          setDetailsOpen(open);
          if (!open) setSelectedTier(null);
        }}
      />
      <ViewInviteCodeDetailsSheet
        code={selectedInviteCode}
        open={inviteDetailsOpen}
        onOpenChange={(open) => {
          setInviteDetailsOpen(open);
          if (!open) setSelectedInviteCode(null);
        }}
      />
    </div>
  );
}
